import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {HeadingSettings} from 'plugins/enhancedHeadings/commands';
import {useCallback, useEffect, useRef, useState} from 'react';

interface GuidanceSettingProps {
  settings: HeadingSettings;
  canEdit: boolean;
  onGuidanceChange: (value: string) => void;
}

export const GuidanceSetting = ({settings, canEdit, onGuidanceChange}: GuidanceSettingProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const initialGuidance = settings.guidance || '';
  const [draftGuidance, setDraftGuidance] = useState(initialGuidance);
  const draftGuidanceRef = useRef(initialGuidance);
  const committedGuidanceRef = useRef(initialGuidance);

  useEffect(() => {
    const nextGuidance = settings.guidance || '';
    const hasPendingLocalEdits = draftGuidanceRef.current !== committedGuidanceRef.current;

    if (!hasPendingLocalEdits) {
      draftGuidanceRef.current = nextGuidance;
      setDraftGuidance(nextGuidance);
    }

    committedGuidanceRef.current = nextGuidance;
  }, [settings.guidance]);

  const commitGuidance = useCallback(() => {
    const nextGuidance = draftGuidanceRef.current;

    if (nextGuidance === committedGuidanceRef.current) {
      return;
    }

    committedGuidanceRef.current = nextGuidance;
    onGuidanceChange(nextGuidance);
  }, [onGuidanceChange]);

  useEffect(() => {
    return () => {
      commitGuidance();
    };
  }, [commitGuidance]);

  // Handle changes from the rich text editor
  const handleEditorChange = useCallback((state: any) => {
    // Use the JSON representation for storage to preserve formatting
    const jsonState = JSON.stringify(state.toJSON());
    draftGuidanceRef.current = jsonState;
    setDraftGuidance(jsonState);
  }, []);

  // Simple event isolation - just stop propagation
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <Box>
      <Text size="small" isBold style={{marginBottom: '4px'}}>
        Guidance
      </Text>
      <div
        ref={containerRef}
        onClick={handleClick}
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
        }}
      >
        <RichTextArea
          value={draftGuidance}
          onChange={handleEditorChange}
          onToggleFocused={(focused) => {
            if (!focused) {
              commitGuidance();
            }
          }}
          placeholder="Add guidance for this section..."
          editable={canEdit}
          minHeight={80}
          maxHeight={200}
          hideToolbar
          editablePaddingX="8"
          editablePaddingY="6"
          disableBorder={true}
          enhancedHeadings={undefined}
          media={false}
          table={false}
          mentions={false}
          links={true}
          autoFocus={false}
        />
      </div>
    </Box>
  );
};
